import { Popup } from '@/components/popup-logic/popup';
import { PopupAdapter } from '@/components/popup-logic/types';
import { createLogger } from '@/lib/logger';

const logger = createLogger('need-help-popup-definition');
const NEED_HELP_POPUP_ID = 'need-help-popup';
const NEED_HELP_POPUP_SELECTOR = '.need-help-popup__section';

class NeedHelpPopupAdapter implements PopupAdapter {
  private id: string;
  private selector: string;
  private shouldShow: () => Promise<boolean>;
  private afterShown: () => Promise<void>;
  private afterHidden: () => Promise<void>;
  private forceShow: boolean;

  constructor({
    id,
    selector,
    shouldShow,
    afterShown,
    afterHidden,
    forceShow,
  }: {
    id: string;
    selector: string;
    shouldShow?: () => Promise<boolean>;
    afterShown?: () => Promise<void>;
    afterHidden?: () => Promise<void>;
    forceShow?: boolean;
  }) {
    this.id = id;
    this.selector = selector;
    this.shouldShow = shouldShow ?? (() => Promise.resolve(true));
    this.afterShown = afterShown ?? (() => Promise.resolve());
    this.afterHidden = afterHidden ?? (() => Promise.resolve());
    this.forceShow = typeof forceShow === 'undefined' ? false : forceShow;
  }

  private getElement(): HTMLElement | null {
    return document.querySelector(this.selector);
  }

  async show(): Promise<boolean> {
    const self = this;

    const shouldShow = await this.shouldShow();
    if (!this.forceShow && !shouldShow) return false;

    const element = this.getElement();
    if (!element) return false;

    let delay = element.dataset.delay;

    if (!delay || isNaN(parseInt(delay))) {
      delay = '0';
    }

    setTimeout(
      async function () {
        await self.afterShown();
      },
      parseInt(delay) * 1000,
    );

    return true;
  }

  async hide(): Promise<boolean> {
    const element = this.getElement();
    if (!element) return true;

    await this.afterHidden();
    return true;
  }

  isVisible(): boolean {
    const element = this.getElement();
    return element?.classList.contains('show-popup') ?? false;
  }

  getId(): string {
    return this.id;
  }

  getForceShow(): boolean {
    return this.forceShow;
  }
}

export default function addDefinitions(): boolean {
  try {
    if (!document.querySelector(NEED_HELP_POPUP_SELECTOR)) {
      return false;
    }

    const adapter = new NeedHelpPopupAdapter({
      id: NEED_HELP_POPUP_ID,
      selector: NEED_HELP_POPUP_SELECTOR,
      shouldShow: () => {
        return Promise.resolve(sessionStorage.getItem('need-help-popup') !== 'true');
      },
      afterShown: () => {
        return new Promise((resolve) => {
          const popupOverlay = document.querySelector(NEED_HELP_POPUP_SELECTOR);
          if (popupOverlay) {
            popupOverlay.classList.add('show-popup');
            document.body.classList.add('lock-scrolling');
          } else {
            logger.error('No Need Help popup found!');
          }
          sessionStorage.setItem('need-help-popup', 'true');
          return resolve();
        });
      },
      afterHidden: () => {
        return new Promise((resolve) => {
          const popupOverlay = document.querySelector(NEED_HELP_POPUP_SELECTOR);
          if (popupOverlay) {
            popupOverlay.classList.remove('show-popup');
          }
          document.body.classList.remove('lock-scrolling');
          return resolve();
        });
      },
    });

    const popup = new Popup(adapter, {
      id: NEED_HELP_POPUP_ID,
      displayName: 'Need Help Popup',
      initiallyVisible: false,
    });

    window.popupMonitor.addPopup(popup);

    return true;
  } catch (e) {
    console.error(e);
    return false;
  }
}
